/** Bundled Shiki themes we load for highlighting. */
export const BUNDLED_SHIKI_THEME_IDS = [
  "github-light",
  "github-dark",
  "solarized-light",
  "solarized-dark",
  "dracula",
  "one-dark-pro",
] as const;

export type BundledShikiTheme = (typeof BUNDLED_SHIKI_THEME_IDS)[number];

/** Concrete code themes (Shiki / CSS), excluding the "auto" preference. */
export const PDF_RESOLVED_CODE_THEME_IDS = [
  ...BUNDLED_SHIKI_THEME_IDS,
  "sepia",
  "soft",
] as const;

export type PdfResolvedCodeTheme =
  (typeof PDF_RESOLVED_CODE_THEME_IDS)[number];

export const PDF_CODE_THEME_IDS = [
  "auto",
  ...PDF_RESOLVED_CODE_THEME_IDS,
] as const;

export type PdfCodeTheme = (typeof PDF_CODE_THEME_IDS)[number];

export type PdfCodeThemeDefinition = {
  id: PdfResolvedCodeTheme;
  label: string;
  /** Bundled Shiki theme used for token colors. */
  shikiTheme: BundledShikiTheme;
  /** Optional background override (e.g. sepia tint over GitHub Light). */
  background?: string;
  /** When true, fenced code blocks omit the document border. */
  borderless?: boolean;
};

export const PDF_CODE_THEMES: readonly PdfCodeThemeDefinition[] = [
  {
    id: "github-light",
    label: "GitHub Light",
    shikiTheme: "github-light",
  },
  {
    id: "github-dark",
    label: "GitHub Dark",
    shikiTheme: "github-dark",
  },
  {
    id: "solarized-light",
    label: "Solarized Light",
    shikiTheme: "solarized-light",
  },
  {
    id: "solarized-dark",
    label: "Solarized Dark",
    shikiTheme: "solarized-dark",
  },
  {
    id: "dracula",
    label: "Dracula",
    shikiTheme: "dracula",
  },
  {
    id: "one-dark-pro",
    label: "One Dark",
    shikiTheme: "one-dark-pro",
  },
  {
    id: "sepia",
    label: "Sepia",
    shikiTheme: "github-light",
    // Match sepia document `--md-code-bg` / tokens.codeBg.
    background: "#ebe1c8",
  },
  {
    id: "soft",
    label: "Soft",
    shikiTheme: "github-light",
    // Dimmed blue-gray fill (GitHub canvas) instead of Shiki's white bg.
    background: "#f6f8fa",
    borderless: true,
  },
] as const;

const codeThemeById = new Map(
  PDF_CODE_THEMES.map((theme) => [theme.id, theme] as const),
);

export function isPdfCodeTheme(value: unknown): value is PdfCodeTheme {
  return (
    typeof value === "string" &&
    PDF_CODE_THEME_IDS.includes(value as PdfCodeTheme)
  );
}

export function isPdfResolvedCodeTheme(
  value: unknown,
): value is PdfResolvedCodeTheme {
  return (
    typeof value === "string" &&
    PDF_RESOLVED_CODE_THEME_IDS.includes(value as PdfResolvedCodeTheme)
  );
}

export function getPdfCodeThemeDefinition(
  id: PdfResolvedCodeTheme,
): PdfCodeThemeDefinition {
  return codeThemeById.get(id) ?? PDF_CODE_THEMES[0];
}

/** Preference default: follow the document theme's paired code theme. */
export const defaultPdfCodeTheme: PdfCodeTheme = "auto";

/** Fallback concrete theme when a document theme is unavailable. */
export const defaultPdfResolvedCodeTheme: PdfResolvedCodeTheme =
  "github-light";

/** CSS class suffix / sanitize allowlist entry for a code theme. */
export function getShikiClassName(codeTheme: PdfResolvedCodeTheme): string {
  return `shiki-${codeTheme}`;
}

export const SHIKI_CLASS_NAMES = PDF_RESOLVED_CODE_THEME_IDS.map(
  getShikiClassName,
);
